import type { ClipboardEvent, ReactElement } from 'react';
import { DropZone, FileTrigger } from 'react-aria-components';
import { ACCEPTED_FILES } from '../ops/shared';
import { Button, Icon } from '../ui';
import { FILE_RULE, pastedName, TOO_MANY_FILES, type FileSource } from './words';
import s from './ActingFor.module.css';

/**
 * The images a paste carries, each named for when it was pasted (D16: "Pasted screenshot, Oct 23
 * 9.12 AM.png"), or none when it carries only text, which is left for the field that has focus.
 * Taking them stops the paste, so nothing else handles the same screenshot twice.
 */
export function takePastedImages(event: ClipboardEvent, now = new Date()): File[] {
  if (event.defaultPrevented) return [];
  const data = event.clipboardData as Partial<DataTransfer> | null;
  const listed = Array.from(data?.files ?? []);
  const fromItems = Array.from(data?.items ?? []).flatMap((item) => {
    const file = item.kind === 'file' ? item.getAsFile() : null;
    return file === null ? [] : [file];
  });
  const images = (listed.length > 0 ? listed : fromItems).filter((f) =>
    f.type.startsWith('image/'),
  );
  if (images.length === 0) return [];
  event.preventDefault();
  event.stopPropagation();
  return images.map((file) => new File([file], pastedName(now, file.type), { type: file.type }));
}

export interface FileDropProps {
  /**
   * Files chosen, dropped or pasted, and where they came from. The step takes what fits in the
   * action's 20, for every way in (a paste anywhere in the step included).
   */
  onFiles: (files: File[], source: FileSource) => void;
  /** Some files didn't fit and the action is full: says so under the zone. */
  refused?: boolean;
  isDisabled?: boolean;
}

/**
 * Drop files here, choose them, or paste a screenshot (APP-148 "The step", D15, D16). The app's
 * one file rule: PDF, Word, Excel, PNG, JPEG, Outlook .msg and .eml, up to 25 MB each. One action
 * carries up to 20 files: past that, a file is refused before it's uploaded, and it says so here.
 */
export function FileDrop({
  onFiles,
  refused = false,
  isDisabled = false,
}: FileDropProps): ReactElement {
  return (
    <div className={s.dropWrap}>
      {/* A paste anywhere in the zone is a screenshot, named for when it was pasted. Caught on the
          way down, so the drop zone's own paste handling never adds it a second time, unnamed. */}
      <div
        onPasteCapture={(event) => {
          if (isDisabled) return;
          const images = takePastedImages(event);
          if (images.length > 0) onFiles(images, 'pasted');
        }}
      >
        <DropZone
          className={s.drop}
          isDisabled={isDisabled}
          aria-label="Drop files here, or paste a screenshot"
          onDrop={(event) => {
            const files = event.items.flatMap((item) => (item.kind === 'file' ? [item] : []));
            void Promise.all(files.map((item) => item.getFile())).then((list) => {
              if (list.length > 0) onFiles(list, 'dropped');
            });
          }}
        >
          <span className={s.dropIcon} aria-hidden="true">
            <Icon name="upload" size={22} />
          </span>
          <span className={s.dropText}>
            <span className={s.dropTitle}>Drop files here, or paste a screenshot</span>
            <span className={s.dropRule}>{FILE_RULE}</span>
          </span>
          <FileTrigger
            acceptedFileTypes={ACCEPTED_FILES}
            allowsMultiple
            onSelect={(list) => {
              const files = Array.from(list ?? []);
              if (files.length > 0) onFiles(files, 'chosen');
            }}
          >
            <Button variant="ghost" size="sm" isDisabled={isDisabled}>
              Choose files
            </Button>
          </FileTrigger>
        </DropZone>
      </div>
      <p className={s.dropRefused} role="status">
        {refused ? (
          <>
            <Icon name="alert" size={14} />
            {TOO_MANY_FILES}
          </>
        ) : null}
      </p>
    </div>
  );
}
